import type { ComponentType } from 'react';
import type { WidgetBlock } from '@shared/conversation-schema';
import { normalizeWidgetActions } from '@shared/conversation-schema';
import { WIDGET_TYPES, WIDGET_SCHEMA_CATALOG } from '@shared/widget-schemas';
import { FallbackWidget } from './FallbackWidget';
import { WidgetActionBar } from './WidgetActionBar';

import { InsightsSummaryWidget } from './InsightsSummaryWidget';
import { ProjectStatusWidget } from './ProjectStatusWidget';
import { RecommendationWidget } from './RecommendationWidget';
import { ProjectConfidenceWidget } from './ProjectConfidenceWidget';
import { StatCardWidget } from './StatCardWidget';
import { AgendaItemWidget } from './AgendaItemWidget';
import { ProjectPlanStepperWidget } from './ProjectPlanStepperWidget';
import { ProjectSnapshotCardWidget } from './ProjectSnapshotCardWidget';
import { ProjectCard } from './ProjectCard';
import { BetSnapshotWidget } from './BetSnapshotWidget';
import { BetsOverviewWidget } from './BetsOverviewWidget';
import { AssumptionSnapshotWidget } from './AssumptionSnapshotWidget';
import { DecisionCardWidget } from './DecisionCardWidget';
import { ProjectActionWidget } from './ProjectActionWidget';
import { RecommendationsCardWidget } from './RecommendationsCardWidget';
import { InterviewsListCardWidget } from './InterviewsListCardWidget';
import { ProjectPlanSnapshotWidget } from './ProjectPlanSnapshotWidget';
import { LinkCardWidget } from './LinkCardWidget';
// 026 — Scoping widget components
import { ScLearntCard } from '../conversation/scoping/ScLearntCard';
import { ScArtifactCard } from '../conversation/scoping/ScArtifactCard';
import { ScChoiceCard } from '../conversation/scoping/ScChoiceCard';
import { ScParticipantTargetsCard } from '../conversation/scoping/ScParticipantTargetsCard';
import { ScResearchPreviewCard } from '../conversation/scoping/ScResearchPreviewCard';
import { ScScopeSummaryCard } from '../conversation/scoping/ScScopeSummaryCard';
// 004-p6 — card_moved breadcrumb marker (id-keyed replace)
import { CardMovedChip } from './CardMovedChip';
import { CARD_MOVED_WIDGET_TYPE } from '@/hooks/v2/widgetReplace';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const widgetRegistry: Record<string, ComponentType<any>> = {
  // 004-p6 — a re-anchored materialized card leaves this chip where it used to be.
  [CARD_MOVED_WIDGET_TYPE]: CardMovedChip,
  [WIDGET_TYPES.INSIGHTS_SUMMARY]: InsightsSummaryWidget,
  [WIDGET_TYPES.PROJECT_STATUS]: ProjectStatusWidget,
  [WIDGET_TYPES.PROJECT_CONFIDENCE]: ProjectConfidenceWidget,
  [WIDGET_TYPES.STAT_CARD]: StatCardWidget,
  [WIDGET_TYPES.AGENDA_ITEM]: AgendaItemWidget,
  [WIDGET_TYPES.PROJECT_PLAN_STEPPER]: ProjectPlanStepperWidget,
  [WIDGET_TYPES.PROJECT_SNAPSHOT_CARD]: ProjectSnapshotCardWidget,
  [WIDGET_TYPES.PROJECT_CARD]: ProjectCard,
  [WIDGET_TYPES.BET_SNAPSHOT]: BetSnapshotWidget,
  [WIDGET_TYPES.BETS_OVERVIEW]: BetsOverviewWidget,
  [WIDGET_TYPES.ASSUMPTION_SNAPSHOT]: AssumptionSnapshotWidget,
  [WIDGET_TYPES.DECISION_CARD]: DecisionCardWidget,
  [WIDGET_TYPES.PROJECT_ACTION]: ProjectActionWidget,
  [WIDGET_TYPES.RECOMMENDATIONS_CARD]: RecommendationsCardWidget,
  [WIDGET_TYPES.INTERVIEWS_LIST]: InterviewsListCardWidget,
  [WIDGET_TYPES.PROJECT_PLAN_SNAPSHOT]: ProjectPlanSnapshotWidget,
  [WIDGET_TYPES.LINK_CARD]: LinkCardWidget,
  // 026 — Scoping widgets
  [WIDGET_TYPES.SC_LEARNT_CARD]: ScLearntCard,
  [WIDGET_TYPES.SC_ARTIFACT_CARD]: ScArtifactCard,
  [WIDGET_TYPES.SC_CHOICE_CARD]: ScChoiceCard,
  [WIDGET_TYPES.SC_PARTICIPANT_TARGETS_CARD]: ScParticipantTargetsCard,
  [WIDGET_TYPES.SC_RESEARCH_PREVIEW_CARD]: ScResearchPreviewCard,
  [WIDGET_TYPES.SC_SCOPE_SUMMARY_CARD]: ScScopeSummaryCard,
};

interface WidgetRendererProps {
  block: WidgetBlock;
  onAction?: (
    widgetId: string,
    action: string,
    payload?: Record<string, unknown>,
    meta?: { text?: string; label?: string },
  ) => void;
}

export function WidgetRenderer({ block, onAction }: WidgetRendererProps) {
  const schema = WIDGET_SCHEMA_CATALOG[block.widget_type];
  let validatedData = block.data;
  if (schema) {
    const result = schema.safeParse(block.data);
    if (!result.success) {
      return (
        <FallbackWidget
          widgetType={block.widget_type}
          widgetId={block.widget_id}
          error={result.error.issues.map(i => i.message).join(', ')}
        />
      );
    }
    validatedData = result.data;
  }

  // Canonical action buttons. n8n content is passed through unvalidated by the
  // gateway, and conversation guides emit the identifier as `id` (not the
  // canonical `action`); `normalizeWidgetActions` aliases `id`→`action` so the
  // buttons fire a defined action id (otherwise every such button is inert).
  // Also repairs already-persisted transcripts hydrated on resume.
  const actions = normalizeWidgetActions(block.actions);

  if (block.widget_type === WIDGET_TYPES.RECOMMENDATION) {
    const base = validatedData as {
      recommendation_id?: string;
      title: string;
      body: string;
      priority: 'high' | 'medium' | 'low';
    };
    return (
      <div>
        <RecommendationWidget
          recommendation_id={base.recommendation_id}
          title={base.title}
          body={base.body}
          priority={base.priority}
          onRecommendationDecision={
            onAction && base.recommendation_id
              ? (decision) => {
                  onAction(block.widget_id, 'recommendation_action', {
                    recommendation_id: base.recommendation_id,
                    decision,
                  });
                }
              : undefined
          }
        />
        {actions.length > 0 && (
          <WidgetActionBar
            actions={actions}
            widgetId={block.widget_id}
            onAction={onAction}
          />
        )}
      </div>
    );
  }

  const Component = widgetRegistry[block.widget_type];

  if (!Component) {
    return (
      <FallbackWidget widgetType={block.widget_type} widgetId={block.widget_id} error="Unknown widget type" />
    );
  }

  // The hero decision/action cards render their action buttons *inside* the
  // card (HeroShell footer), so they receive `actions`/`onAction` as props and
  // suppress the external WidgetActionBar. Every other widget keeps the bar.
  const rendersActionsInCard = IN_CARD_ACTION_WIDGETS.has(block.widget_type);
  const inCardActionProps = rendersActionsInCard
    ? {
        actions,
        onAction: onAction
          ? (action: string, payload?: Record<string, unknown>, meta?: { text?: string; label?: string }) =>
              onAction(block.widget_id, action, payload, meta)
          : undefined,
      }
    : undefined;

  return (
    <div>
      <Component {...(validatedData as Record<string, unknown>)} {...inCardActionProps} />
      {!rendersActionsInCard && actions.length > 0 && (
        <WidgetActionBar
          actions={actions}
          widgetId={block.widget_id}
          onAction={onAction}
        />
      )}
    </div>
  );
}

/** Widgets that surface `block.actions` inside the card rather than as a bar below. */
const IN_CARD_ACTION_WIDGETS = new Set<string>([
  WIDGET_TYPES.DECISION_CARD,
  WIDGET_TYPES.PROJECT_ACTION,
  // 026 — Scoping widgets with in-card actions
  WIDGET_TYPES.SC_CHOICE_CARD,
  WIDGET_TYPES.SC_SCOPE_SUMMARY_CARD,
  WIDGET_TYPES.SC_ARTIFACT_CARD,
]);
